// 明信片图鉴（P6-POLISH-ENG-002 / 设计规格 07 §H）。
//
// 替换旧的按月分组文字列表（PostcardListView）—— 老板反馈"效果不好"。
// 目标：获得一张点亮一张，和礼物图鉴同构。
//
// 关键设计点（与旧列表的差异）：
//   · 顺序**固定按 POSTCARD_NOTES**，不按获得时间排 —— 图鉴必须稳定，否则每收一张就重排。
//   · 按目的地分 4 组 + 中文小标题，"这个地方的 3 张齐了没"一眼可读。
//   · 未获得卡**无文字**（不剧透）+ disabled 不弹 toast。
//   · 历史存档同一 noteId 可能有多条 → 去重点亮，点开打开 stampedAt **最早**那条。
//
// 本组件不接触 game state（全靠 props），纯渲染。
import { useState } from 'react';
import type { DestinationId, PostcardEntry } from '../../save/types';
import { POSTCARD_NOTES } from '../../runtime/postcardNotes';
import { assetIndex, POSTCARD_ASSET_BY_NOTE, type AssetId } from '../../assets/assetIndex';

interface PostcardCodexViewProps {
  mailbox: readonly PostcardEntry[];
  selectedId: string | null;
  /** 点某张已获得的卡 → 由父级先同步 markPostcardRead 再开详情（沿用既有点击时序）。 */
  onSelect: (entryId: string) => void;
}

// 目的地中文小标题（07 §H.2）。
const DEST_LABEL: Record<DestinationId, string> = {
  riverside: '河岸',
  forest: '森林',
  hill: '山丘',
  town: '小镇',
};

const DEST_ORDER: readonly DestinationId[] = ['riverside', 'forest', 'hill', 'town'];

interface CodexCard {
  noteId: string;
  stamp: string;
  body: string;
  /** 该 noteId 对应的 mailbox 条目（取 stampedAt 最早那条）；null = 尚未获得。 */
  entry: PostcardEntry | null;
}

interface CodexGroup {
  destinationId: DestinationId;
  label: string;
  cards: CodexCard[];
}

// 按目的地分 4 组，组内顺序严格跟随 POSTCARD_NOTES（不按获得时间）。
function buildGroups(mailbox: readonly PostcardEntry[]): CodexGroup[] {
  // noteId → 最早那条（历史存档可能有重复条目）
  const earliestByNote = new Map<string, PostcardEntry>();
  for (const entry of mailbox) {
    const prev = earliestByNote.get(entry.noteId);
    if (!prev || entry.stampedAt < prev.stampedAt) earliestByNote.set(entry.noteId, entry);
  }

  return DEST_ORDER.map((destinationId) => ({
    destinationId,
    label: DEST_LABEL[destinationId],
    cards: POSTCARD_NOTES.filter((n) => n.destinationId === destinationId).map((n) => ({
      noteId: n.id,
      stamp: n.stamp,
      body: n.body,
      entry: earliestByNote.get(n.id) ?? null,
    })),
  }));
}

export function PostcardCodexView({ mailbox, selectedId, onSelect }: PostcardCodexViewProps) {
  // 图片加载失败 → 降级灰块（复用 PostcardDetail 的 imgFailed 模式，07 §H-E1）
  const [failed, setFailed] = useState<ReadonlySet<string>>(new Set());
  const groups = buildGroups(mailbox);
  const ownedCount = new Set(mailbox.map((e) => e.noteId)).size;

  return (
    <div className="postcard-codex">
      {ownedCount === 0 && (
        // 07 §H-E4：0 张时给一行引导（与礼物空态同构），但 12 张灰卡照常显示 —— 牵引价值
        <p className="postcard-codex__empty">还没收到明信片。</p>
      )}
      {groups.map((group) => {
        const ownedInGroup = group.cards.filter((c) => c.entry !== null).length;
        return (
          <section
            key={group.destinationId}
            className="postcard-codex__group"
            aria-label={`${group.label}的明信片，已收到 ${ownedInGroup} 张，共 ${group.cards.length} 张`}
          >
            <h3 className="postcard-codex__dest">{group.label}</h3>
            <div className="postcard-codex__grid">
              {group.cards.map((card) => {
                const obtained = card.entry !== null;
                const assetId = POSTCARD_ASSET_BY_NOTE[card.noteId];
                const src = assetId ? assetIndex[assetId as AssetId] : undefined;

                if (!obtained) {
                  return (
                    // 未获得：灰卡 + 剪影，**无文字**（不剧透）；disabled 不 Tab 到也不弹 toast（07 §H-E8）
                    <div
                      key={card.noteId}
                      className="postcard-codex__card postcard-codex__card--locked"
                      role="img"
                      aria-label="尚未收到的明信片"
                    >
                      <span className="postcard-codex__silhouette" aria-hidden="true" />
                    </div>
                  );
                }

                const done = failed.has(card.noteId);
                return (
                  <button
                    key={card.noteId}
                    type="button"
                    className={
                      selectedId === card.entry!.id
                        ? 'postcard-codex__card postcard-codex__card--selected'
                        : 'postcard-codex__card'
                    }
                    aria-label={`明信片：寄自 ${card.stamp}。${card.body}`}
                    onClick={() => onSelect(card.entry!.id)}
                  >
                    {src && !done ? (
                      <img
                        className="postcard-codex__img"
                        src={src}
                        alt=""
                        aria-hidden="true"
                        // ⚠️ 07 §H.7 性能：12 张图约 13MB，必须 lazy + async 解码；
                        //   aspect-ratio 由 CSS 固定占位，避免图片到位前的布局抖动。
                        loading="lazy"
                        decoding="async"
                        onError={() => setFailed((prev) => new Set(prev).add(card.noteId))}
                      />
                    ) : (
                      // 降级：灰块 + stamp 文字兜底（图片 404 / 未落地）
                      <span className="postcard-codex__fallback">{card.stamp}</span>
                    )}
                    <span className="postcard-codex__stamp">{card.stamp}</span>
                    {!card.entry!.isRead && (
                      // 未读圆点（07 §H.5）：与礼物卡的「新」文字角标刻意不同 ——
                      // 明信片用圆点、礼物用「新」字，避免两个 tab 的标记语言混淆。
                      <span className="postcard-codex__dot" aria-hidden="true" />
                    )}
                  </button>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}
